import { inject } from "@angular/core";
import {
	ActivatedRouteSnapshot,
	CanActivateFn,
	Router,
	RouterStateSnapshot,
} from "@angular/router";
import { PermissionsService } from "../services/permissions.service";
import { Auth } from "@angular/fire/auth";
import { UserRole } from "../models/permissions.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OrganizationsService } from "../services/organizations.service";
import { firstValueFrom } from "rxjs";

export const rolesGuard: CanActivateFn = async (
	route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot,
): Promise<boolean> => {
	const permissionsService = inject(PermissionsService);
	const auth = inject(Auth);
	const router = inject(Router);
	const snackBar = inject(MatSnackBar);
	const organizationsService = inject(OrganizationsService);

	const currentUser = auth.currentUser;
	if (!currentUser) {
		router.navigate(["/auth/login"]);
		return false;
	}

	const userId = currentUser.uid;
	const requiredRoles = route.data["roles"] as UserRole[];
	const context = route.data["permissionContext"] as
		| "global"
		| "organization"
		| "partner"
		| "partners"
		| "organizations";

	if (!requiredRoles || !context) {
		console.error("Route is missing required roles or permission context");
		snackBar.open("Invalid route configuration", "Close", { duration: 5000 });
		return false;
	}

	// Check if user is global admin first - they have access to everything
	const isGlobalAdmin = await permissionsService.isGlobalAdmin(userId);
	if (isGlobalAdmin) {
		return true;
	}

	switch (context) {
		case "global":
			const globalPermissions = await permissionsService.getGlobalPermissions(userId);
			const hasGlobalAccess = !!(
				globalPermissions.hasAccess &&
				globalPermissions.role &&
				requiredRoles.includes(globalPermissions.role)
			);
			if (!hasGlobalAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasGlobalAccess;

		case "organizations":
			// Check if user has access to any organization with required roles
			const allOrgPermissions = await permissionsService.getAllOrganizationAccess(userId);

			// Check if user has any of the required roles in any organization
			const hasAnyOrgAccess = allOrgPermissions.some((perm) => {
				if (!perm.hasAccess || !perm.data || !perm.data.roles) {
					return false;
				}

				// Check if any of the required roles are present in the roles map
				const hasRole = requiredRoles.some((requiredRole) => {
					// Convert role to lowercase with underscores
					const roleKey = requiredRole.toLowerCase();
					return perm.data.roles[roleKey] === true;
				});
				return hasRole;
			});

			// If no org access, check partner access
			if (!hasAnyOrgAccess) {
				const allPartnerPermissions = await permissionsService.getAllPartnerAccess(userId);

				const hasAnyPartnerAccess = allPartnerPermissions.some((perm) => {
					if (!perm.hasAccess || !perm.data || !perm.data.roles) {
						return false;
					}

					// Check if any of the required roles are present in the roles map
					const hasRole = requiredRoles.some((requiredRole) => {
						const roleKey = requiredRole.toLowerCase();
						return perm.data.roles[roleKey] === true;
					});
					return hasRole;
				});

				if (!hasAnyPartnerAccess) {
					snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
						duration: 5000,
					});
				}
				return hasAnyPartnerAccess;
			}

			return hasAnyOrgAccess;

		case "organization":
			const organizationId = route.params["id"];
			if (!organizationId) {
				console.error("Organization ID is required for organization context");
				snackBar.open("Invalid organization ID", "Close", { duration: 5000 });
				return false;
			}

			// First check organization permissions
			const orgPermissions = await permissionsService.getOrganizationPermissions(
				organizationId,
				userId,
			);

			const hasOrgAccess = !!(
				orgPermissions.hasAccess &&
				orgPermissions.roles &&
				orgPermissions.roles.some((role) => requiredRoles.includes(role as UserRole))
			);

			if (hasOrgAccess) {
				return true;
			}

			// If no org access, check if user is partner admin or coach of any partner associated with this org
			const partners = await firstValueFrom(
				organizationsService.getOrganizationPartners(organizationId),
			);

			if (partners.length === 0) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
				return false;
			}

			// Check partner permissions for each partner
			for (const partner of partners) {
				const partnerPermissions = await permissionsService.getPartnerPermissions(
					partner.id!,
					userId,
				);

				// Check if user is either partner admin or coach
				const hasPartnerAccess = !!(
					partnerPermissions.hasAccess &&
					partnerPermissions.data?.roles &&
					(partnerPermissions.data.roles.partner_admin === true ||
						partnerPermissions.data.roles.coach === true)
				);

				if (hasPartnerAccess) {
					return true;
				}
			}

			snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
				duration: 5000,
			});
			return false;

		case "partners":
			// Check if user has access to any partner with required roles
			const allPartnerPermissions = await permissionsService.getAllPartnerAccess(userId);
			const hasAnyPartnerAccess = allPartnerPermissions.some(
				(perm) =>
					perm.hasAccess &&
					perm.data &&
					perm.data.roles &&
					Object.keys(perm.data.roles).some(
						(role) =>
							perm.data.roles[role] === true &&
							requiredRoles.includes(role as UserRole),
					),
			);
			if (!hasAnyPartnerAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasAnyPartnerAccess;

		case "partner":
			const partnerId = route.params["id"];
			if (!partnerId) {
				console.error("Partner ID is required for partner context");
				snackBar.open("Invalid partner ID", "Close", { duration: 5000 });
				return false;
			}
			const partnerPermissions = await permissionsService.getPartnerPermissions(
				partnerId,
				userId,
			);
			const hasPartnerAccess = !!(
				partnerPermissions.hasAccess &&
				partnerPermissions.data &&
				partnerPermissions.data.roles &&
				Object.keys(partnerPermissions.data.roles).some(
					(role) =>
						partnerPermissions.data.roles[role] === true &&
						requiredRoles.includes(role as UserRole),
				)
			);
			if (!hasPartnerAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasPartnerAccess;

		default:
			console.error("Invalid permission context");
			snackBar.open("Invalid permission context", "Close", { duration: 5000 });
			return false;
	}
};
